import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Bell, BellOff, ChevronDown, ChevronUp } from "lucide-react";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "./ui/popover";
import { MobileSheet, MobileSheetCloseButton, MobileSheetContent, MobileSheetTitle, MobileSheetTrigger } from "./ui/mobile-sheet";
import useIsMobile from "../hooks/useIsMobile";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import useVisiblePolling from "../hooks/useVisiblePolling";
import { hasPermission } from "../lib/permissions";
import {
  PAGE_SIZE, arrivalMessage, badgeLabel, groupItems, leadItems, mergeItems, newArrivals, relativeTime, safeLink, unreadTotal,
} from "../lib/notifications";

const KEY_LAST_SEEN = "jamora_new_leads_last_seen";
const KEY_SOUND = "jamora_notifications_sound";
const POLL_INTERVAL = 30000; // 30s

// Web Audio API "ding": no external asset needed
function playDing() {
  try {
    if (localStorage.getItem(KEY_SOUND) === "off") return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    // Two-tone chime: C6 → E6
    [
      { freq: 1046.5, start: 0, dur: 0.18 },
      { freq: 1318.5, start: 0.12, dur: 0.25 },
    ].forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.22, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    });
    setTimeout(() => ctx.close(), 800);
  } catch (e) {
    // silent fail
  }
}

function BellRow({ item, onOpen, nested = false }) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(item)}
        data-testid={`notif-item-${item.id}`}
        data-unread={item.read_at ? "false" : "true"}
        className={`flex w-full items-start gap-3 py-3 text-left border-b border-slate-100 hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:outline-none ${nested ? "pl-9 pr-4" : "px-4"}`}
      >
        <span aria-hidden="true" className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${item.read_at ? "bg-transparent" : "bg-jamora"}`} />
        <span className="min-w-0 flex-1">
          <span className={`block text-sm text-slate-900 line-clamp-1 ${item.read_at ? "font-normal" : "font-semibold"}`}>{item.title}</span>
          {item.summary && <span className="block text-xs text-slate-500 mt-0.5 line-clamp-2">{item.summary}</span>}
          <span className="block text-2xs text-slate-400 mt-0.5">{relativeTime(item.created_at)}</span>
        </span>
      </button>
    </li>
  );
}

export default function NotificationsBell() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const canSeeProspects = hasPermission(user, "prospects:view");
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ loading: true, error: false, items: [], unread: 0, hasMore: false });
  const [leads, setLeads] = useState([]);
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const [loadingMore, setLoadingMore] = useState(false);
  const [soundOn, setSoundOn] = useState(localStorage.getItem(KEY_SOUND) !== "off");
  const lastSeenRef = useRef(localStorage.getItem(KEY_LAST_SEEN) || new Date().toISOString());
  const knownRef = useRef(null); // ids announced or present at the first reading: null until then
  const leadsReadyRef = useRef(false);

  const announce = useCallback((arrivals) => {
    if (arrivals.length === 0) return;
    playDing(); // once per batch
    const first = arrivals[0];
    const link = safeLink(first.link);
    toast.success(arrivalMessage(arrivals), {
      action: arrivals.length === 1 && link ? { label: "Ouvrir", onClick: () => navigate(link) } : undefined,
    });
  }, [navigate]);

  const loadInbox = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/inbox", { params: { limit: PAGE_SIZE } });
      setState((prev) => {
        // Pages already loaded below the first one stay; the first page is refreshed in place.
        const fresh = data.items || [];
        const freshIds = new Set(fresh.map((i) => i.id));
        const older = prev.items.slice(PAGE_SIZE).filter((i) => !freshIds.has(i.id));
        const wasFirstPageOnly = prev.items.length <= PAGE_SIZE;
        return { loading: false, error: false, items: [...fresh, ...older], unread: data.unread_count || 0, hasMore: wasFirstPageOnly ? !!data.has_more : prev.hasMore };
      });
      const arrivals = newArrivals(knownRef.current, data.items || []);
      knownRef.current = new Set([...(knownRef.current || []), ...(data.items || []).map((i) => i.id)]);
      announce(arrivals);
    } catch (e) {
      setState((prev) => ({ ...prev, loading: false, error: prev.items.length === 0 }));
    }
  }, [announce]);

  const loadLeads = useCallback(async () => {
    if (!canSeeProspects) return;
    try {
      const { data } = await api.get("/prospects/recent", { params: { since: lastSeenRef.current, limit: 20 } });
      const fresh = data.prospects || [];
      // Advance the polling cursor on every successful check, announced or not; otherwise the same prospect comes back as
      // "new" on every poll, re-triggering the toast and sound in a loop.
      const now = new Date().toISOString();
      lastSeenRef.current = now;
      localStorage.setItem(KEY_LAST_SEEN, now);
      const items = leadItems(fresh);
      setLeads((prev) => {
        const ids = new Set(prev.map((x) => x.id));
        return [...items.filter((f) => !ids.has(f.id)), ...prev].slice(0, 20);
      });
      if (!leadsReadyRef.current) {
        leadsReadyRef.current = true;
        return;
      }
      announce(items);
    } catch (e) {
      // silent retry on the next tick
    }
  }, [canSeeProspects, announce]);

  const refresh = useCallback(() => {
    loadInbox();
    loadLeads();
  }, [loadInbox, loadLeads]);

  useEffect(() => {
    refresh();
  }, [refresh]);
  useVisiblePolling(refresh, POLL_INTERVAL);

  const loadMore = async () => {
    const last = state.items[state.items.length - 1];
    if (!last || loadingMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/notifications/inbox", { params: { limit: PAGE_SIZE, before: last.created_at } });
      setState((prev) => {
        const ids = new Set(prev.items.map((i) => i.id));
        return { ...prev, items: [...prev.items, ...(data.items || []).filter((i) => !ids.has(i.id))], hasMore: !!data.has_more };
      });
    } catch (e) {
      toast.error("Impossible de charger les notifications plus anciennes.");
    } finally {
      setLoadingMore(false);
    }
  };

  const markAllRead = async () => {
    const now = new Date().toISOString();
    localStorage.setItem(KEY_LAST_SEEN, now);
    lastSeenRef.current = now;
    setLeads([]);
    setState((prev) => ({ ...prev, unread: 0, items: prev.items.map((i) => (i.read_at ? i : { ...i, read_at: now })) }));
    try {
      await api.post("/notifications/inbox/read-all");
    } catch (e) {
      toast.error("Impossible de marquer les notifications comme lues.");
      loadInbox();
    }
  };

  const openItem = async (item) => {
    const link = safeLink(item.link);
    setOpen(false);
    if (item.id.startsWith("lead:")) {
      setLeads((prev) => prev.filter((l) => l.id !== item.id));
    } else if (!item.read_at) {
      const now = new Date().toISOString();
      setState((prev) => ({ ...prev, unread: Math.max(0, prev.unread - 1), items: prev.items.map((i) => (i.id === item.id ? { ...i, read_at: now } : i)) }));
      api.post(`/notifications/inbox/${item.id}/read`).catch(() => loadInbox());
    }
    if (link) navigate(link);
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem(KEY_SOUND, next ? "on" : "off");
    if (next) playDing();
  };

  const items = mergeItems(state.items, leads);
  const count = unreadTotal(state.unread, leads);
  const hasNew = count > 0;
  const Menu = isMobile ? MobileSheet : Popover;
  const MenuTrigger = isMobile ? MobileSheetTrigger : PopoverTrigger;
  const MenuContent = isMobile ? MobileSheetContent : PopoverContent;

  return (
    <Menu open={open} onOpenChange={setOpen}>
      <MenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid="btn-notifications"
          className="relative h-10 w-10"
          aria-label={hasNew ? `Notifications, ${count} non lue${count > 1 ? "s" : ""}` : "Notifications"}
        >
          <Bell size={20} className={hasNew ? "bell-shake text-jamora" : "text-slate-500"} />
          {hasNew && (
            <span
              data-testid="notif-badge"
              className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full bg-primary text-white text-2xs font-semibold flex items-center justify-center"
            >
              {badgeLabel(count)}
            </span>
          )}
        </Button>
      </MenuTrigger>
      <MenuContent
        {...(isMobile ? { "aria-describedby": undefined } : { align: "end" })}
        className={isMobile ? "overflow-hidden" : "w-96 p-0"}
        data-testid="notifications-panel"
      >
        {isMobile && <MobileSheetTitle className="sr-only">Notifications</MobileSheetTitle>}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="font-display font-semibold text-sm text-slate-900">Notifications</div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleSound}
              data-testid="btn-toggle-sound"
              title={soundOn ? "Désactiver le son" : "Activer le son"}
              aria-label={soundOn ? "Désactiver le son" : "Activer le son"}
              className="text-slate-500 hover:text-jamora"
            >
              {soundOn ? <Bell size={16} /> : <BellOff size={16} />}
            </button>
            {hasNew && (
              <button onClick={markAllRead} data-testid="btn-mark-all-seen" className="text-xs text-jamora hover:underline">
                Tout marquer lu
              </button>
            )}
            {isMobile && <MobileSheetCloseButton />}
          </div>
        </div>
        {state.loading ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400" data-testid="notif-loading">Chargement…</div>
        ) : state.error && items.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500" data-testid="notif-error">
            Impossible de charger vos notifications.
            <div className="mt-2"><button onClick={loadInbox} className="text-xs text-jamora hover:underline">Réessayer</button></div>
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500" data-testid="notif-empty">
            Rien de nouveau pour le moment.
            <div className="text-xs text-slate-400 mt-1">Vos tâches, tickets, validations et échéances apparaîtront ici.</div>
          </div>
        ) : (
          <ul className="max-h-[65dvh] md:max-h-[28rem] overflow-y-auto scrollbar-thin" data-testid="notif-list">
            {groupItems(items).map((entry) => {
              if (entry.type === "item") return <BellRow key={entry.item.id} item={entry.item} onOpen={openItem} />;
              const expanded = openGroups.has(entry.id);
              return (
                <li key={entry.id}>
                  <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => setOpenGroups((prev) => { const next = new Set(prev); if (next.has(entry.id)) next.delete(entry.id); else next.add(entry.id); return next; })}
                    data-testid={`notif-group-${entry.event_type}`}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left border-b border-slate-100 bg-slate-50/60 hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:outline-none"
                  >
                    <span aria-hidden="true" className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${entry.unread ? "bg-jamora" : "bg-transparent"}`} />
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm text-slate-900 line-clamp-1 ${entry.unread ? "font-semibold" : "font-normal"}`}>{entry.title} ({entry.items.length})</span>
                      <span className="block text-xs text-slate-500 mt-0.5">{expanded ? "Masquer le détail" : "Afficher le détail"}</span>
                    </span>
                    {expanded ? <ChevronUp size={16} className="mt-0.5 text-slate-400" aria-hidden /> : <ChevronDown size={16} className="mt-0.5 text-slate-400" aria-hidden />}
                  </button>
                  {expanded && <ul>{entry.items.map((item) => <BellRow key={item.id} item={item} onOpen={openItem} nested />)}</ul>}
                </li>
              );
            })}
            {state.hasMore && (
              <li className="px-4 py-3 text-center">
                <button onClick={loadMore} disabled={loadingMore} data-testid="notif-more" className="text-xs text-jamora hover:underline disabled:opacity-50">
                  {loadingMore ? "Chargement…" : "Voir les notifications plus anciennes"}
                </button>
              </li>
            )}
          </ul>
        )}
      </MenuContent>
    </Menu>
  );
}
